import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { resetInbox } from '@app/store/slices/inboxSlice';
import {
  ResetPasswordRequest,
  login,
  loginWithGoogle,
  LoginRequest,
  signUp,
  SignUpRequest,
  resetPassword,
  verifySecurityCode,
  SecurityCodePayload,
  NewPasswordData,
  setNewPassword,
  signOut,
  TokenData,
} from '@app/api/auth.api';
import { deleteToken, deleteUser, persistToken, persistUser, readToken } from '@app/services/localStorage.service';
import { message } from 'antd';
import { UserModel } from '@app/interfaces/user';

export interface AuthSlice {
  tokenData: TokenData | null;
}

const initialState: AuthSlice = {
  tokenData: readToken(),
};

const persistLogin = (res: { tokenData: TokenData; user: any }): TokenData => {
  const { tokenData, user } = res || {};
  const data = user?.data;

  persistUser({
    id: data.id,
    name: data.name,
    email: data.email,
    // sign_in thường trả employee_id; Google SSO trả object employee.{id}
    sales_employee: data.employee_id ?? data.employee?.id ?? false,
    employee: data.employee ?? (data.employee_id ? { id: data.employee_id } : undefined),
    // Lưu luôn vai trò khi phản hồi có: thiếu nó thì màn chủ phải chờ /api/me
    // mới biết đây là admin hay nhân viên bán.
    roles: data.roles,
  } as UserModel);

  persistToken(tokenData);
  message.success('You signed in successfully.');

  return tokenData;
};

export const doLogin = createAsyncThunk('auth/doLogin', async (loginPayload: LoginRequest) =>
  login(loginPayload).then(persistLogin),
);

export const doLoginWithGoogle = createAsyncThunk('auth/doLoginWithGoogle', async (idToken: string) => {
  try {
    return await loginWithGoogle(idToken).then(persistLogin);
  } catch (e: any) {
    const msg = e?.response?.data?.errors?.[0] || e?.response?.data?.error || e?.message || 'Đăng nhập Google thất bại';
    throw new Error(msg);
  }
});

export const doSignUp = createAsyncThunk('auth/doSignUp', async (signUpPayload: SignUpRequest) =>
  signUp(signUpPayload),
);

export const doResetPassword = createAsyncThunk(
  'auth/doResetPassword',
  async (resetPassPayload: ResetPasswordRequest) => resetPassword(resetPassPayload),
);

export const doVerifySecurityCode = createAsyncThunk(
  'auth/doVerifySecurityCode',
  async (securityCodePayload: SecurityCodePayload) => verifySecurityCode(securityCodePayload),
);

export const doSetNewPassword = createAsyncThunk('auth/doSetNewPassword', async (newPasswordData: NewPasswordData) =>
  setNewPassword(newPasswordData),
);

export const doLogout = createAsyncThunk('auth/doLogout', (_: void, { dispatch }) => {
  // Xoá token phía client ngay (Logout điều hướng về trang đăng nhập liền), rồi mới báo server
  // huỷ token bằng bản đã giữ lại.
  const token = readToken();
  deleteToken();
  deleteUser();
  import('@app/hooks/useUserRoles').then(({ clearUserRolesCache }) => clearUserRolesCache());
  import('@app/services/cable.service').then(({ disconnectCable }) => disconnectCable());
  dispatch(resetInbox());
  signOut(token);

  message.success('You signed out successfully.');
});

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(doLogin.fulfilled, (state, action) => {
      state.tokenData = action.payload;
    });
    builder.addCase(doLoginWithGoogle.fulfilled, (state, action) => {
      state.tokenData = action.payload;
    });
    builder.addCase(doLogout.fulfilled, (state) => {
      state.tokenData = null;
    });
  },
});

export default authSlice.reducer;
